import React, { useState } from "react";
import { Button, Card, Form, Input, Segmented, Select, Upload, message } from "antd";
import { InboxOutlined, FileExcelOutlined, FileTextOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import { generateMclFile } from "../../services/ApiService";
import "./MclGenerator.css";

// Pag-IBIG/HDMF MCL remittance file generator for the 8x8 Philippines
// Branch Office payroll team -- confirmed with the user, 2026-10-30. The
// 4 types share the same fixed-width byte layout on the backend (see
// services/mcl_service.py), just a different 2-char Payment Type code in
// the header and a different filename segment -- segments here verified
// against 4 real, bank-approved September 2026 .mcl files, 2026-10-31
// ("Contribution" has no filename suffix at all, unlike the other 3).
const MCL_TYPE_SEGMENTS = {
  Contribution: "HDMFFile",
  "Calamity Loan": "HDMFLoanReport_CL",
  "Short Term Loan": "HDMFLoanReport_ST",
  "Savings M2": "HDMFFileM2",
};

const MONTH_OPTIONS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i).map((y) => ({
  value: String(y),
  label: String(y),
}));

const MclGenerator = () => {
  const [form] = Form.useForm();
  const [mclType, setMclType] = useState("Contribution");
  const [file, setFile] = useState(null);
  const [generating, setGenerating] = useState(false);

  const uploadProps = {
    accept: ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    maxCount: 1,
    fileList: file ? [file] : [],
    beforeUpload: (selected) => {
      setFile({ uid: selected.uid, name: selected.name, originFileObj: selected });
      return false;
    },
    onRemove: () => setFile(null),
  };

  const handleGenerate = async (values) => {
    if (!file) {
      message.error("Please upload the Excel data file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file.originFileObj);
    formData.append("mcl_type", mclType);
    formData.append("employer_name", values.employerName.trim());
    formData.append("employer_address", values.employerAddress.trim());
    formData.append("year", values.year);
    formData.append("month", values.month);

    setGenerating(true);
    try {
      const blob = await generateMclFile(formData);
      if (blob?.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        message.error(parsed.responseMessage || "Failed to generate MCL file");
        return;
      }

      const typeSegment = MCL_TYPE_SEGMENTS[mclType];
      const monthAbbr = MONTH_ABBR[Number(values.month) - 1];
      const filename = `8x8IPBO_${typeSegment}_${monthAbbr}${values.year}.mcl`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("MCL file generated");
    } catch (error) {
      console.error("Failed to generate MCL file:", error);
      message.error("Failed to generate MCL file");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="main-box">
      <Sidebar activeId={15} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header title="MCL Generator" />
          <div style={{ flex: 1, padding: "1.5rem", boxSizing: "border-box", overflowY: "auto" }}>
            <Card className="mcl-card" bordered={false}>
              <div className="mcl-card-header">
                <div className="mcl-card-icon">
                  <SafetyCertificateOutlined />
                </div>
                <div>
                  <div className="mcl-card-title">Pag-IBIG / HDMF Remittance File</div>
                  <div className="mcl-card-subtitle">
                    Generate the fixed-width .mcl file for 8x8 Philippines Branch Office from the Excel data export.
                  </div>
                </div>
              </div>

              <div className="mcl-section-label">File Type</div>
              <Segmented
                className="mcl-type-segmented"
                block
                size="large"
                value={mclType}
                onChange={setMclType}
                options={Object.keys(MCL_TYPE_SEGMENTS)}
              />

              <Form
                form={form}
                layout="vertical"
                className="mcl-form"
                onFinish={handleGenerate}
                requiredMark={false}
              >
                <div className="mcl-section-label">Employer Details</div>
                <div className="mcl-form-grid">
                  <Form.Item
                    name="employerName"
                    label="Employer Name"
                    rules={[{ required: true, message: "Employer Name is required" }]}
                  >
                    <Input placeholder="e.g. PMC8X8 International Philippines Branch Office" />
                  </Form.Item>
                  <Form.Item
                    name="employerAddress"
                    label="Employer Address"
                    rules={[{ required: true, message: "Employer Address is required" }]}
                  >
                    <Input placeholder="e.g. Unit 1101, 11F World Plaza, 5th Ave" />
                  </Form.Item>
                </div>

                <div className="mcl-section-label">Filing Period</div>
                <div className="mcl-form-grid mcl-form-grid-narrow">
                  <Form.Item name="year" label="Year" rules={[{ required: true, message: "Select a year" }]}>
                    <Select placeholder="Select Year" options={YEAR_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="month" label="Month" rules={[{ required: true, message: "Select a month" }]}>
                    <Select placeholder="Select Month" options={MONTH_OPTIONS} />
                  </Form.Item>
                </div>

                <div className="mcl-section-label">Data File</div>
                <Upload.Dragger {...uploadProps} className="mcl-upload-dragger">
                  {file ? (
                    <div className="mcl-upload-file">
                      <FileExcelOutlined className="mcl-upload-file-icon" />
                      <div className="mcl-upload-file-name">{file.name}</div>
                      <div className="mcl-upload-file-hint">Click or drag a new file to replace</div>
                    </div>
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Click or drag the Excel data file to upload</p>
                      <p className="ant-upload-hint">Use the HDMF_MCL_Excel_Template.xlsx format</p>
                    </>
                  )}
                </Upload.Dragger>

                <Form.Item className="mcl-submit-row">
                  <Button
                    type="primary"
                    size="large"
                    icon={<FileTextOutlined />}
                    loading={generating}
                    htmlType="submit"
                  >
                    Generate MCL
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MclGenerator;
